// FaqSection.jsx
import React from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const faqs = [
  {
    id: "panel1",
    q: "How often should i have my eyes tested?",
    a:
      "WaveLight® Plus uses advanced, AI-guided laser technology to create a fully customized treatment plan for your unique eyes. By mapping the entire optical system with ultra-precise 3D imaging, it reshapes the cornea in seconds — all without blades, stitches, or pain.",
  },
  {
    id: "panel2",
    q: "What are the signs and symptoms of corneal disease?",
    a:
      "Common signs include pain, blurry vision, sensitivity to light, redness, and tearing. Early diagnosis helps preserve vision — consult an eye specialist if you notice any of these.",
  },
  {
    id: "panel3",
    q: "Glasses required after laser eye surgery?",
    a:
      "Most patients achieve significant reduction in dependence on glasses. Some may still need reading glasses or slight correction for certain distances depending on age and eye condition.",
  },
  {
    id: "panel4",
    q: "Is the procedure painful?",
    a:
      "The procedure is performed under topical anesthesia (eye drops) so patients generally experience minimal discomfort during treatment. Recovery varies but many feel comfortable within 24 hours.",
  },
];

const FaqSection = () => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState("panel1");

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        py: { md: 12, xs: 8 },
        px: { md: 14, xs: 3 },
        backgroundColor: "#fff",
      }}
    >
      {/* Title */}
      <Box sx={{ textAlign: "center", mb: { md: 5, xs: 4 } }}>
        <Typography
          sx={{ fontSize: { md: "32px", xs: "22px" }, fontWeight: 800 }}
        >
          Frequently Asked Question
        </Typography>

        {/* small underline */}
        <Box
          sx={{
            height: 3,
            width: 84,
            backgroundColor: "#2EAD9F",
            borderRadius: 2,
            margin: "10px auto 0",
          }}
        />

        <Typography sx={{ fontSize: 13, mt: 1, color: "#666" }}>
          Got questions? We’ve got answers.
        </Typography>
      </Box>

      {/* Accordions container */}
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        {faqs.map((f, idx) => {
          const isFirst = idx === 0;
          const isExpanded = expanded === f.id;
          return (
            <Accordion
              key={f.id}
              expanded={isExpanded}
              onChange={handleChange(f.id)}
              elevation={0}
              sx={{
                boxShadow: "0 6px 18px rgba(19,22,24,0.04)",
                borderRadius: "10px",
                mb: 2.5,
                "&:before": { display: "none" }, // remove default divider
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#2EAD9F" }} />}
                aria-controls={`${f.id}-content`}
                id={`${f.id}-header`}
                sx={{
                  minHeight: 64,
                  "& .MuiAccordionSummary-content": {
                    margin: 0,
                    alignItems: "center",
                  },
                  px: { md: 4, xs: 3 },
                  py: { md: 1.5, xs: 1 },
                  // first (expanded by default) style: teal header background and rounded top corners
                  ...(isExpanded && {
                    backgroundColor: "#BEEDE6", // light teal for expanded header
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                  }),
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>{f.q}</Typography>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  px: { md: 4, xs: 3 },
                  pt: 2.5,
                  pb: 3,
                  backgroundColor: isExpanded ? "#fff" : "#fff",
                  borderBottomLeftRadius: "10px",
                  borderBottomRightRadius: "10px",
                }}
              >
                <Typography sx={{ color: "#5f6468", lineHeight: 1.8 }}>
                  {f.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
};

export default FaqSection;
